import { computed, inject, Pipe, PipeTransform, signal, untracked, type OnDestroy, type Signal } from '@angular/core';
import { findFootnoteIdsInString, FOOTNOTE_MATCH_REGEX, isDefined } from '@hyundai/shared-lib';
import { Store } from '@ngrx/store';
import { FootnotesFeature } from '../+state';

@Pipe({
    name: 'footnotes',
    standalone: true,
    pure: true,
})
export class FootnotesPipe implements PipeTransform, OnDestroy {
    protected readonly store = inject(Store);

    numberedFootnotes = this.store.selectSignal(FootnotesFeature.state.selectNumberedFootnotes);

    text = signal<string>('');
    removeOnly = signal(false);

    footnoteIds: string[] = [];

    replaceFootnotesText = computed(() => {
        const text = this.text();
        const numberedFootnotes = this.numberedFootnotes();
        const removeOnly = this.removeOnly();
        if (!text) {
            return text;
        }

        return text.replace(FOOTNOTE_MATCH_REGEX, (_match, idMatch) => {
            if (removeOnly) {
                return '';
            }
            const ids = idMatch.split(',');
            const footnotes = numberedFootnotes.filter((f) => ids.includes(f.id));
            return footnotes
                .map((f) => (f ? `<sup class="footnote f_${f.id}">${f.sign}</sup>` : undefined))
                .filter(isDefined)
                .join('<sup>,</sup>');
        });
    });

    transform(value: string, removeOnly = false): Signal<string> {
        untracked(() => {
            if (!removeOnly) {
                const footnoteIds = findFootnoteIdsInString(value);
                const newFootnoteIds = footnoteIds.filter((id) => !this.footnoteIds.includes(id));
                const removedFootnoteIds = this.footnoteIds.filter((id) => !footnoteIds.includes(id));
                if (newFootnoteIds.length > 0) {
                    this.store.dispatch(
                        FootnotesFeature.actions.registerActiveFootnotes({ footnotes: newFootnoteIds }),
                    );
                }
                if (removedFootnoteIds.length > 0) {
                    this.store.dispatch(
                        FootnotesFeature.actions.unregisterActiveFootnotes({ footnotes: removedFootnoteIds }),
                    );
                }
                this.footnoteIds = footnoteIds;
            }
            this.removeOnly.set(removeOnly);
            this.text.set(value);
        });
        return this.replaceFootnotesText;
    }

    ngOnDestroy(): void {
        this.store.dispatch(FootnotesFeature.actions.unregisterActiveFootnotes({ footnotes: this.footnoteIds }));
    }
}
